import config from '../config'

export const INVITE_STORAGE_KEY = 'INVITE_CODE'

// 旧版小程序码 scene 前缀（2026-07-20 ~ 2026-08-17 期间生成，码已永久缓存在 ums_member.mp_qrcode）
const LEGACY_SCENE_PREFIX = 'agent_'

/**
 * 微信场景值恒为 4 位数字（1001/1007/1047/1089…）
 * 邀请码是 8 位数字的 member_no，形状不同，用于剔除被误当邀请码的场景值
 */
export function isSceneValue(value) {
	return /^\d{4}$/.test(String(value || ''))
}

/**
 * 邀请码归一化：解码 → 兼容旧版 agent_{memberNo} scene → 剔除场景值/undefined 等脏值
 */
export function normalizeInviteCode(raw) {
	if (!raw) {
		return ''
	}
	let code = raw
	try {
		code = decodeURIComponent(code)
	} catch (e) {
		// 无 %xx 编码的普通字符串不会抛错，此处仅兜底
	}
	code = String(code).trim()
	if (code.indexOf(LEGACY_SCENE_PREFIX) === 0) {
		code = code.slice(LEGACY_SCENE_PREFIX.length)
	}
	if (!code || code === 'undefined' || code === 'null' || isSceneValue(code)) {
		return ''
	}
	return code
}

/**
 * App 层（App.onLaunch / App.onShow）读取邀请码
 * 注意：App 层 options.scene 是微信场景值（1001/1047/1089…），小程序码的 scene 在 options.query.scene；
 * H5 端 uni-app 运行时还会把 scene 固定成 1001。故 App 层只认 options.query，避免场景值被写进本地存储
 */
export function readInviteFromAppOptions(options) {
	if (!options) {
		return ''
	}
	const query = options.query || {}
	return normalizeInviteCode(query.inviteCode || query.scene || '')
}

/**
 * 从页面启动参数中读取邀请码
 * 来源：分享卡片路径 query（inviteCode）、小程序码 scene（URL 编码）
 * 页面级 onLoad 的 options.scene 就是小程序码的 scene 参数，可直接取
 */
export function readInviteFromOptions(options) {
	if (!options) {
		return ''
	}
	let code = ''
	if (options.query && options.query.inviteCode) {
		code = options.query.inviteCode
	}
	// 页面级 onLoad options：query 参数直接是顶层属性（好友/朋友圈卡片落地页）
	if (!code && options.inviteCode) {
		code = options.inviteCode
	}
	if (!code) {
		code = options.scene || (options.query && options.query.scene) || ''
	}
	return normalizeInviteCode(code)
}

/**
 * 记录邀请码到本地存储 + Vuex（App.vue 冷启动/热启动共用，只认 App 层 query 参数）
 */
export function recordAppInvite(options) {
	const code = readInviteFromAppOptions(options)
	if (code) {
		uni.setStorageSync(INVITE_STORAGE_KEY, code)
		commitInviteCode(code)
	}
}

/**
 * 记录邀请码到本地存储 + Vuex（页面 onLoad 使用）
 */
export function recordInvite(options) {
	const code = readInviteFromOptions(options)
	if (code) {
		uni.setStorageSync(INVITE_STORAGE_KEY, code)
		commitInviteCode(code)
	}
}

/**
 * 清理历史被场景值（1089/1001…）、undefined、agent_ 前缀污染的本地邀请码
 * App.onLaunch 调用一次；不清理的话老用户本地存储会继续回填到注册/登录页输入框
 */
export function sanitizeStoredInviteCode() {
	const stored = uni.getStorageSync(INVITE_STORAGE_KEY)
	if (stored && !normalizeInviteCode(stored)) {
		uni.removeStorageSync(INVITE_STORAGE_KEY)
		commitInviteCode('')
		return ''
	}
	return stored || ''
}

/**
 * 登录/注册成功后清理邀请码，避免下次误绑定
 */
export function clearInviteCode() {
	uni.removeStorageSync(INVITE_STORAGE_KEY)
	commitInviteCode('')
}

function commitInviteCode(code) {
	try {
		// 延迟 require 避免循环依赖
		const store = require('../store').default
		if (store && store.commit) {
			store.commit('setInviteCode', code)
		}
	} catch (e) {
		console.warn('share: commit inviteCode fail', e)
	}
}

/**
 * 核心页公共分享 mixin
 * - onLoad 记录分享卡片/小程序码携带的邀请码
 * - onShow 按登录态设置原生"···"分享菜单（小程序 showShareMenu/hideShareMenu，H5 微信 JS-SDK）
 * 各核心页需自行定义 onShareAppMessage，用 shareAppMessageConfig 构造返回值
 */
const shareMixin = {
	data() {
		return {
			shareMpQrcode: '' // 个人小程序码（分享封面）
		}
	},
	onLoad(options) {
		this.recordInvite(options)
	},
	onShow() {
		this.setupNativeShare()
	},
	methods: {
		// 记录邀请码
		recordInvite(options) {
			recordInvite(options)
		},
		// 登录/注册成功后清理邀请码
		clearInviteCode() {
			clearInviteCode()
		},
		// 确保个人小程序码已生成（分享封面），复用后端 mp_qrcode 缓存
		ensureMpQrcode() {
			const that = this
			const userInfo = uni.getStorageSync('userInfo') || {}
			if (userInfo.mpQrcode) {
				that.shareMpQrcode = userInfo.mpQrcode
				return Promise.resolve(userInfo.mpQrcode)
			}
			// 并发去重：同一实例共享同一个进行中的请求
			if (that._mpQrcodePromise) {
				return that._mpQrcodePromise
			}
			that._mpQrcodePromise = new Promise((resolve) => {
				if (!userInfo.accessToken) {
					that._mpQrcodePromise = null
					resolve('')
					return
				}
				uni.request({
					url: config.baseUrl + '/member/qrcode',
					method: 'GET',
					header: {
						'Authorization': 'Bearer ' + userInfo.accessToken
					},
					success: (res) => {
						that._mpQrcodePromise = null
						if (res.statusCode === 200 && res.data && res.data.code === 200 && res.data.data) {
							that.shareMpQrcode = res.data.data
							userInfo.mpQrcode = res.data.data
							uni.setStorageSync('userInfo', userInfo)
							resolve(res.data.data)
						} else {
							resolve('')
						}
					},
					fail: () => {
						that._mpQrcodePromise = null
						resolve('')
					}
				})
			})
			return that._mpQrcodePromise
		},
		// 按登录态设置原生"···"分享：仅登录可转发（携带邀请码/个人二维码封面）
		setupNativeShare() {
			const userInfo = uni.getStorageSync('userInfo') || {}
			const isLogin = !!(userInfo && userInfo.accessToken && userInfo.id)
			if (isLogin) {
				// 登录时提前拉取个人小程序码，作为分享封面（后端有缓存，未生成才请求）
				this.ensureMpQrcode()
			}













			// 仅微信浏览器可用（main.js 中 boolForWeChatH5() 才挂载 $jwx）；
			// 普通浏览器（Chrome/Edge）调试时无 $jwx，静默跳过——微信分享菜单本就不存在
			if (isLogin && this.$jwx && typeof this.$jwx.configWeiXin === 'function') {
				const that = this
				that.ensureMpQrcode().then((qr) => {
					that.$jwx.configWeiXin((jweixin) => {
						const link = that.buildShareLink()
						if (jweixin.updateAppMessageShareData) {
							jweixin.updateAppMessageShareData({
								title: '东江惠购 康养平台',
								desc: '健康生活 · 轻松分享 · 智慧养生',
								link: link,
								imgUrl: qr
							})
						}
						if (jweixin.updateTimelineShareData) {
							jweixin.updateTimelineShareData({
								title: '东江惠购 康养平台',
								link: link,
								imgUrl: qr
							})
						}
					})
				})
			}

		},
		// H5 分享链接：当前 URL 去掉 hash/code/state 后追加 inviteCode（与签名 URL 无关，微信只校验签名 URL）
		buildShareLink() {

			if (typeof window !== 'undefined' && window.location) {
				const userInfo = uni.getStorageSync('userInfo') || {}
				try {
					const u = new URL(window.location.href)
					u.searchParams.delete('inviteCode')
					u.searchParams.delete('code')
					u.searchParams.delete('state')
					if (userInfo.memberNo) {
						u.searchParams.set('inviteCode', userInfo.memberNo)
					}
					u.hash = ''
					return u.href
				} catch (e) {
					return window.location.href.split('#')[0]
				}
			}

			return ''
		},
		/**
		 * 构造小程序 onShareAppMessage 返回值
		 * 登录时 path 追加 inviteCode、imageUrl 用个人小程序码；未登录返回默认配置（无推广）
		 */
		shareAppMessageConfig(extra = {}) {
			const userInfo = uni.getStorageSync('userInfo') || {}
			const isLogin = !!(userInfo && userInfo.accessToken && userInfo.id)
			let path = extra.path || '/pages/index/index'
			if (isLogin && userInfo.memberNo) {
				const sep = path.indexOf('?') > -1 ? '&' : '?'
				path += sep + 'inviteCode=' + userInfo.memberNo
			}
			const res = {
				title: extra.title || '东江惠购 康养平台',
				path: path
			}
			if (extra.imageUrl) {
				res.imageUrl = extra.imageUrl
			} else if (this.shareMpQrcode) {
				res.imageUrl = this.shareMpQrcode
			}
			return res
		},
		/**
		 * 构造页面 onShareTimeline 返回值（朋友圈分享，分享的是当前页面，无 path 字段）
		 * 登录时 query 追加 inviteCode、imageUrl 用个人小程序码；未登录返回默认配置（无推广）
		 * query 为 key=value&key2=value2 字符串
		 */
		shareTimelineConfig(extra = {}) {
			const userInfo = uni.getStorageSync('userInfo') || {}
			const isLogin = !!(userInfo && userInfo.accessToken && userInfo.id)
			let query = extra.query || ''
			if (isLogin && userInfo.memberNo) {
				query += (query ? '&' : '') + 'inviteCode=' + userInfo.memberNo
			}
			const res = {
				title: extra.title || '东江惠购 康养平台'
			}
			if (query) {
				res.query = query
			}
			if (extra.imageUrl) {
				res.imageUrl = extra.imageUrl
			} else if (this.shareMpQrcode) {
				res.imageUrl = this.shareMpQrcode
			}
			return res
		}
	}
}

export default shareMixin
